
import { useState, useEffect, useRef } from "react";
import { Music } from "lucide-react";
import { 
  Accordion, 
  AccordionContent, 
  AccordionItem, 
  AccordionTrigger 
} from "@/components/ui/accordion";

const WhyChooseUsSection = () => {
  const [expandedItem, setExpandedItem] = useState<string | null>("item-1");
  const sectionRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);
  
  const accordionItems = [
    {
      id: "item-1",
      question: "What makes YouAux unique?",
      answer: "YouAux stands out by offering fast music releases, 24/7 support, and global distribution so your music reaches listeners everywhere, faster than ever. We provide real-time analytics, transparent royalties, and AI-powered marketing tools to help you grow your audience."
    },
    {
      id: "item-2",
      question: "How do we support independent artists?",
      answer: "We empower independent artists by distributing their music globally while they keep full ownership and creative control. We offer transparent royalties, powerful marketing tools, and real-time insights to help artists grow."
    },
    {
      id: "item-3",
      question: "Why trust YouAux with your music?",
      answer: "With a reputation for <strong>transparency and reliability</strong>, we prioritize your success by delivering efficient, artist-focused solutions. Your music journey is in trusted hands."
    },
    {
      id: "item-4",
      question: "What benefits will you gain by working with us?",
      answer: "From <strong>global exposure</strong> to advanced marketing tools, our services are designed to help you stand out in the competitive music industry while retaining your creative freedom."
    }
  ];

  return (
    <section ref={sectionRef} className="py-8 md:py-20 relative overflow-hidden bg-[#000003]">
      {/* Background elements*/}
      {/* <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute bottom-0 left-1/4 w-96 h-96 rounded-full bg-cosmic-600/10 blur-[100px]" />
        <div className="absolute top-0 right-1/4 w-96 h-96 rounded-full bg-celestial-600/10 blur-[100px]" /> */}
        
        {/* Diagonal lines */}
        {/* <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-full h-full border-b border-cosmic-500/5 -skew-y-6"></div>
          <div className="absolute top-10 left-0 w-full h-full border-b border-celestial-500/5 -skew-y-12"></div>
        </div>
      </div> */}

      <div className="container mx-auto px-4">
        <div className="h-px w-full bg-gradient-to-r from-transparent via-ophira-yellow/30 to-transparent mb-8" />
        <div className={`text-center max-w-3xl mx-auto mb-10 md:mb-12 transition-all duration-700 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}>
          <div className="ophira-section-label justify-center">WHY CHOOSE US</div>
          <h2 className="text-[32px] md:text-3xl lg:text-4xl font-bold mb-1">
            Why We're the Right
          </h2>
          <h2 className="text-[32px] md:text-3xl lg:text-4xl font-bold mb-2 relative inline-block">
            <span className="celestial-gradient-text relative z-10">Choice for You</span>
            <span className="absolute -z-10 left-0 bottom-0 w-full h-3 bg-celestial-500/20 -skew-y-3"></span>
          </h2>
        </div>
        <div className="h-px w-full bg-gradient-to-r from-transparent via-ophira-yellow/30 to-transparent mb-10 md:mb-12" />

        <div className="max-w-3xl mx-auto">
          <Accordion 
            type="single" 
            collapsible 
            value={expandedItem} 
            onValueChange={setExpandedItem} 
            className={`celestial-card p-4 md:p-6 transition-all duration-700 delay-200 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'
            }`}
          >
            {accordionItems.map((item, index) => (
              <AccordionItem 
                key={item.id} 
                value={item.id} 
                className="border-b border-border/50 last:border-0"
              >
                <AccordionTrigger className="flex py-3 md:py-4 text-left text-base md:text-lg font-medium hover:no-underline group">
                  <div className="flex items-center gap-2 md:gap-3">
                    <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-celestial-500/10 flex items-center justify-center group-hover:bg-celestial-500/20 transition-colors duration-300">
                      <Music className="h-4 w-4 md:h-5 md:w-5 text-celestial-400" />
                    </div>
                    <span className="group-hover:text-celestial-400 transition-colors duration-300 text-lg md:text-xl">
                      {item.question}
                    </span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="py-1 md:py-0 pb-4 px-3 md:px-4 text-md md:text-[16px] text-white/65 bg-[#000003] rounded-lg my-2">
                  <div dangerouslySetInnerHTML={{ __html: item.answer }} />
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
};

export default WhyChooseUsSection;
